function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function(r2) {
      return Object.getOwnPropertyDescriptor(e, r2).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), true).forEach(function(r2) {
      _defineProperty(e, r2, t[r2]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function(r2) {
      Object.defineProperty(e, r2, Object.getOwnPropertyDescriptor(t, r2));
    });
  }
  return e;
}
function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: true, configurable: true, writable: true }) : e[r] = t, e;
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t)
    return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i)
      return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
import { jsx } from "panel";
import last from "lodash/last";
import sum from "lodash/sum";
import isNumber from "lodash/isNumber";
import { SegmentationLimitInfoEl, SegmentationLimitLearnMoreUrl } from "common/charts/misc/segmentation-limit-info";
import { ButtonEl } from "common/components/button";
import { ButtonTheme } from "common/components/button/constants";
import { CheckboxEl } from "common/components/checkbox";
import { CHECKBOX_CELL_PADDING_LEFT, CssClass, DEFAULT_TOOLTIP_MAX_WIDTH, FOOTER_ROW_HEIGHT, GROUP_HEADER_MIN_WIDTH, STICKY_COLUMNS_BORDER_WIDTH } from "common/components/data-table/constants";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { ExperimentalTooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { SortBy, SortOrder } from "common/types/reports/bookmark";
import { isURL } from "common/util/url";
import { renderSegmentLabel } from "common/templates/mixins";
import { InputEl } from "common/components/input";
import { InputSize, InputType } from "common/components/input/types";
import { BannerEl } from "common/components/banner";
import { BannerSize, BannerTheme, BannerType } from "common/components/banner/types";
import { HORIZONTAL_CONTENT_PADDING_STYLE } from "iron/common/report/layout/banner-layout";
import { FilterBarEl } from "common/components/filter-bar";
import { getTooltipFromLabel } from "common/report/util/properties";
import { SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl } from "common/components/text-highlight";
import { LIFT_TOOLTIP_COMPARE, LiftType } from "common/report/util/lift/types";
import { formatDateRanges } from "common/report/insights/util";
import { CommonDataTableFields, isTimeComparisonColumnType, RowType, TimeComparisonColumnType, TruncatableTextType } from "./types";
import { areIntersectingRanges, formatPercentForDataTable, getFormattedValue, getSummedColumnWidth, hasSegmentMetricMetadata, isNumberStyleColumn, isSameCellByContext, isSegmentColumn, isTextStyleColumn } from "./utils";
import segmentMetricOverflowTooltipStyle from "./segment-metric-overflow-tooltip.inline.styl";
import viewNMenuStyle from "./view-n-menu.inline.styl";
function renderViewNMenu(_ref) {
  let {
    tableEl,
    segmentIdx
  } = _ref;
  const isColumnSort = tableEl.state.sorting.sortBy === SortBy.Column;
  const hasViewNLimit = tableEl.hasViewNLimit({
    segmentIdx
  });
  return /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-content", "data-sentry-component": "renderViewNMenu", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("style", null, viewNMenuStyle), /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-limit-section" }, /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-title" }, "".concat(tableEl.getViewNSortOrderLabel({
    segmentIdx
  }), " ").concat(isColumnSort ? "Segments" : "Values")), /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-description" }, "Choose the number of ".concat(isColumnSort ? "segments" : "rows", " you want to see in the chart")), /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-input-section", class: {
    "show-all-mode": !hasViewNLimit
  } }, /* @__PURE__ */ jsx(InputEl, { sel: ".view-n-menu-input", attrs: {
    "auto-focus": hasViewNLimit,
    "not-focusable": !hasViewNLimit,
    size: InputSize.Medium,
    type: InputType.Number,
    min: "1",
    maxlength: 5
  }, on: {
    focus: () => tableEl.handleViewNLimitInputFocus({
      segmentIdx
    }),
    blur: () => tableEl.handleViewNLimitInputBlur(),
    change: (ev) => tableEl.handleViewNLimitInputChange({
      segmentIdx,
      value: Number.parseInt(ev.detail.value, 10)
    })
  }, props: {
    value: tableEl.getViewNLimitInputValue({
      segmentIdx
    }).toString()
  }, "data-sentry-element": "InputEl", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ jsx("div", { sel: ".view-n-menu-input-label" }, isColumnSort ? "segments" : "rows"))), /* @__PURE__ */ jsx("div", { sel: ".view-n-show-all-section" }, /* @__PURE__ */ jsx(CheckboxEl, { attrs: {
    checked: !hasViewNLimit
  }, on: {
    click: () => tableEl.handleViewNShowAllClick({
      segmentIdx
    })
  }, "data-sentry-element": "CheckboxEl", "data-sentry-source-file": "template.tsx" }, "Show all ", isColumnSort ? "segments" : "rows")));
}
function renderViewNControl(_ref2) {
  let {
    tableEl,
    segmentIdx
  } = _ref2;
  const {
    sortColumn,
    sortOrder
  } = tableEl.getViewNSortInfo({
    segmentIdx
  });
  const sortFieldStr = isSegmentColumn(sortColumn) ? sortColumn.title : sortColumn.sortSelectTitle;
  const sortOrderStr = sortOrder === SortOrder.Ascending ? "ascending" : "descending";
  return /* @__PURE__ */ jsx(SelectEl, { sel: ".view-n-select", attrs: _objectSpread({
    "experimental-positioning": true,
    theme: SelectTheme.Tertiary,
    "always-show-caret": true,
    disabled: tableEl.isBoardMode(),
    label: tableEl.getViewNLabel({
      segmentIdx
    })
  }, tableEl.hasViewNLimit({
    segmentIdx
  }) && {
    tooltip: {
      placement: Placement.RightCenter,
      "max-width": "".concat(DEFAULT_TOOLTIP_MAX_WIDTH, "px"),
      text: "Sorted by ".concat(sortFieldStr, " ").concat(sortOrderStr)
    }
  }), props: {
    // body slot doesn't work with portalled mp-drop-menu, so need to provide render function instead
    dropdownBodyRenderer: () => renderViewNMenu({
      tableEl,
      segmentIdx
    })
  }, on: {
    menuChange: (ev) => tableEl.handleViewNMenuChange({
      ev,
      segmentIdx
    })
  }, "data-sentry-element": "SelectEl", "data-sentry-component": "renderViewNControl", "data-sentry-source-file": "template.tsx" });
}
function TitleCell(_ref3) {
  let {
    tableEl,
    column
  } = _ref3;
  if (isTimeComparisonColumnType(column) && column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Past) {
    return null;
  }
  const {
    print
  } = tableEl.getContext("i18n");
  const context = {
    rowType: RowType.Header,
    columnIdx: column.columnIdx
  };
  const isHoveredCell = isSameCellByContext(context, tableEl.state.hoveredCellContext);
  const {
    sorting
  } = tableEl.state;
  const isCurrSortField = sorting.sortBy === SortBy.Value && sorting.valueField === column.field;
  let headerTooltipEl = null;
  if (isHoveredCell) {
    let tooltipContent = null;
    if (column.tooltip) {
      tooltipContent = column.tooltip;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderTitle) {
      tooltipContent = column.title;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderSubtitle) {
      tooltipContent = column.subtitle;
    }
    headerTooltipEl = tooltipContent ? /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
      // The tooltip of the first data column header is constrained to prevent it from being obscured by the
      // sticky column to its left.
      "max-width": !column.sticky && column.subsetColumnIdx === 0 ? "".concat(column.computedWidth, "px") : "".concat(DEFAULT_TOOLTIP_MAX_WIDTH, "px"),
      placement: Placement.Top,
      "always-show": true
    }, params: {
      text: tooltipContent
    } }) : null;
  }
  const titleEl = /* @__PURE__ */ jsx("div", { class: {
    [CssClass.HeaderTitleText]: true,
    "cell-text": true
  }, on: {
    pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderTitle),
    pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
  } }, column.titlePrefix ? /* @__PURE__ */ jsx("span", { sel: ".header-title-prefix" }, column.titlePrefix) : null, /* @__PURE__ */ jsx("span", { attrs: {
    elref: "headerCellTitle"
  } }, print(column.title)));
  let subtitleEl = null;
  if (column.subtitle) {
    subtitleEl = /* @__PURE__ */ jsx("div", { class: {
      [CssClass.HeaderSubtitleText]: true,
      "cell-text": true
    }, on: {
      pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderSubtitle),
      pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
    } }, column.subtitle);
  } else if (isSegmentColumn(column) && tableEl.canRenderViewNControl({
    segmentColumn: column
  })) {
    subtitleEl = renderViewNControl({
      tableEl,
      segmentIdx: column.segmentIndex
    });
  } else if (tableEl.hasColumnWithSubtitle()) {
    subtitleEl = /* @__PURE__ */ jsx("div", { sel: ".header-subtitle-placeholder" });
  }
  let titleLabelContents = null;
  if (column.title) {
    if (!column.isSortable) {
      titleLabelContents = titleEl;
    } else if (isSegmentColumn(column)) {
      titleLabelContents = /* @__PURE__ */ jsx(SelectEl, { attrs: {
        "menu-width": 190,
        "experimental-positioning": true
      }, on: {
        change: tableEl.helpers.handleSortSelect
      }, props: {
        sections: tableEl.getSortSelectSections(column)
      } }, /* @__PURE__ */ jsx("div", { attrs: {
        slot: "trigger"
      }, sel: ".sort-label.within-select-el" }, titleEl, isSegmentColumn(column) ? /* @__PURE__ */ jsx("span", { sel: ".segment-cardinality" }, column.segmentCardinality) : null, /* @__PURE__ */ jsx("div", { sel: ".sort-icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
        icon: "caret-down"
      } }))));
    } else {
      titleLabelContents = /* @__PURE__ */ jsx("div", { sel: ".sort-label.value-sort" }, titleEl, isCurrSortField ? sorting.sortOrder === SortOrder.Ascending ? /* @__PURE__ */ jsx("div", { sel: ".sort-icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
        icon: "sort-value-asc"
      } })) : /* @__PURE__ */ jsx("div", { sel: ".sort-icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
        icon: "sort-value-desc"
      } })) : null);
    }
  }
  const hasSelectAllCheckbox = column.hasCheckbox;
  const titleContents = /* @__PURE__ */ jsx("div", { sel: ".mp-table-title-wrapper", on: _objectSpread({}, isSegmentColumn(column) ? {} : {
    click: () => tableEl.helpers.handleHeaderTitleClick(column)
  }) }, hasSelectAllCheckbox ? (() => {
    const {
      numSelected,
      atSelectionLimit
    } = tableEl.helpers.getGlobalSelectionStats();
    return /* @__PURE__ */ jsx("div", { sel: ".checkbox-wrapper" }, /* @__PURE__ */ jsx(CheckboxEl, { sel: ".select-segment-checkbox", attrs: {
      checked: atSelectionLimit,
      "unchecked-icon": numSelected > 0 ? "dash" : ""
    }, style: {
      width: "auto"
    }, on: {
      click: tableEl.helpers.toggleAllSelected,
      pointerenter: tableEl.helpers.handleSelectAllPointerEnter,
      pointerleave: tableEl.helpers.handleSelectAllPointerLeave
    } }), tableEl.state.isSelectAllHovered ? /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
      placement: Placement.RightCenter,
      "always-show": true,
      text: atSelectionLimit ? "Deselect All" : "Select Top Segments"
    } }) : null);
  })() : null, titleLabelContents, column.icon ? /* @__PURE__ */ jsx("div", { sel: ".mp-table-title-cell-icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
    icon: column.icon
  } })) : null);
  const gridCellWidth = isTimeComparisonColumnType(column) && column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current ? 2 : 1;
  const gridRowStart = (Number.isFinite(tableEl.getPreTitleRowHeight()) ? 1 : 0) + 1;
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = "".concat(gridRowStart, " / ").concat(gridColStart, " / ").concat(gridRowStart + 1, " / ").concat(gridColStart + gridCellWidth);
  return /* @__PURE__ */ jsx("div", { sel: ".mp-table-cell.title-cell", class: {
    "mp-table-sortable-column": column.isSortable,
    "mp-text-cell": isTextStyleColumn(column),
    "mp-number-cell": isNumberStyleColumn(column),
    "segment-col": isSegmentColumn(column),
    "first-segment-col": column.field === "".concat(CommonDataTableFields.SegmentPrefix, "0"),
    "first-in-group": column.first,
    "last-in-group": column.last,
    "overall-conversion-header": column.field === "overall",
    "first-column": column.leftmost,
    "last-column": column.rightmost,
    "last-sticky": column.lastSticky,
    "last-segment-col": isSegmentColumn(column) && column.lastSegment,
    "sticky-column": column.sticky,
    "has-tooltip": Boolean(headerTooltipEl || tableEl.state.isSelectAllHovered),
    "curr-sort-field": isCurrSortField,
    "has-subtitle": Boolean(subtitleEl)
  }, style: {
    "grid-area": gridAreaStyle
  }, on: {
    pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
    pointerleave: tableEl.helpers.handleCellPointerLeave
  }, attrs: {
    title: column.label || ""
  }, "data-sentry-component": "TitleCell", "data-sentry-source-file": "template.tsx" }, titleContents, subtitleEl, headerTooltipEl);
}
function CellTypeCheckbox(_ref4) {
  let {
    row,
    tableEl,
    atSelectionLimit,
    context
  } = _ref4;
  const options = tableEl.helpers.getCheckboxState({
    row,
    atSelectionLimit
  });
  const shouldShowTooltip = isSameCellByContext(context, tableEl.state.hoveredCellContext);
  return !row.isComparisonRow && !Number.isFinite(row.groupedRowIdx) ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(CheckboxEl, { sel: ".select-segment-checkbox", attrs: options.attrs, on: {
    click: (ev) => !options.attrs.disabled && tableEl.helpers.toggleSelected(ev, row)
  }, "data-sentry-element": "CheckboxEl", "data-sentry-source-file": "template.tsx" }), shouldShowTooltip && options.selectDisabledReason && options.attrs.disabled ? /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
    "always-show": true,
    placement: Placement.RightCenter,
    text: options.selectDisabledReason
  } }) : null) : /* @__PURE__ */ jsx("div", { sel: ".checkbox-placeholder", "data-sentry-component": "CellTypeCheckbox", "data-sentry-source-file": "template.tsx" });
}
function CellTypeCollapsible(_ref5) {
  let {
    row,
    tableEl
  } = _ref5;
  return /* @__PURE__ */ jsx("div", { sel: ".mp-table-collapsible-wrapper", "data-sentry-component": "CellTypeCollapsible", "data-sentry-source-file": "template.tsx" }, !Number.isFinite(row.groupedRowIdx) ? /* @__PURE__ */ jsx(ButtonEl, { attrs: {
    icon: row.isCollapsed ? "caret-right" : "caret-down",
    theme: ButtonTheme.Tertiary
  }, on: {
    click: (ev) => tableEl.helpers.collapsibleClicked(ev, row)
  } }) : null);
}
function renderSegmentMetricMetadataRow(_ref6) {
  let {
    metadata,
    dataFormatType,
    liftType,
    metricTitle,
    prefix,
    i18n,
    inlineMetric = false
  } = _ref6;
  const {
    comparisonOverall,
    liftOverall,
    metricOverall
  } = metadata;
  const formattedMetricOverall = getFormattedValue(metricOverall, dataFormatType);
  const formattedComparisonOverall = getFormattedValue(comparisonOverall, dataFormatType);
  const additionalSummaryText = Number.isFinite(liftOverall) ? " (".concat(LIFT_TOOLTIP_COMPARE, " ").concat(formattedComparisonOverall, ")") : "";
  return /* @__PURE__ */ jsx("div", { sel: ".segment-metric-total-row", "data-sentry-component": "renderSegmentMetricMetadataRow", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("span", { sel: ".segment-metric-total-label" }, prefix, inlineMetric ? " ".concat(i18n.print(metricTitle)) : ""), /* @__PURE__ */ jsx("span", { sel: ".segment-metric-total-value" }, formattedMetricOverall), Number.isFinite(liftOverall) && /* @__PURE__ */ jsx(TextHighlightEl, { sel: ".segment-metric-lift-value", attrs: {
    text: "".concat(liftOverall > 0 && liftType !== LiftType.Overall ? "+" : "").concat(formatPercentForDataTable(liftOverall)),
    theme: liftOverall === 0 || liftType === LiftType.Overall ? TEXT_HIGHLIGHT_THEME.NEUTRAL : liftOverall > 0 ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE,
    size: SIZE.SMALL
  } }), inlineMetric ? additionalSummaryText : /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
    placement: Placement.RightCenter,
    text: "_".concat(prefix, " ").concat(metricTitle, "_ ").concat(formattedMetricOverall).concat(additionalSummaryText)
  } }));
}
function renderSegmentMetricMetadatas(_ref7) {
  let {
    dataColumns,
    liftType,
    metricMetadatas,
    numAvailableRows,
    i18n
  } = _ref7;
  const metadataEntries = metricMetadatas.map((metadata, index) => {
    return {
      dataFormatType: dataColumns[index].dataFormatType,
      liftType,
      metadata,
      metricTitle: dataColumns[index].title,
      prefix: dataColumns[index].titlePrefix
    };
  }).filter((entry) => hasSegmentMetricMetadata(entry.metadata));
  const hasHiddenRows = metadataEntries.length > numAvailableRows;
  const numMetricRowsToRender = hasHiddenRows ? numAvailableRows - 1 : metricMetadatas.length;
  const visibleMetadatas = metadataEntries.slice(0, numMetricRowsToRender);
  const hiddenMetadatas = metadataEntries.slice(numMetricRowsToRender);
  return /* @__PURE__ */ jsx("div", { sel: ".segment-metric-totals-section", "data-sentry-component": "renderSegmentMetricMetadatas", "data-sentry-source-file": "template.tsx" }, visibleMetadatas.map((entry) => renderSegmentMetricMetadataRow(_objectSpread(_objectSpread({}, entry), {}, {
    i18n
  }))), (hiddenMetadatas === null || hiddenMetadatas === void 0 ? void 0 : hiddenMetadatas.length) > 0 ? /* @__PURE__ */ jsx("div", { sel: ".segment-metric-total-more-section" }, /* @__PURE__ */ jsx("span", { sel: ".segment-metric-total-more" }, hiddenMetadatas.length, " more"), /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
    placement: Placement.RightCenter
  } }, /* @__PURE__ */ jsx("div", { sel: ".segment-metric-overflow-content" }, /* @__PURE__ */ jsx("style", null, segmentMetricOverflowTooltipStyle), hiddenMetadatas.map((entry) => renderSegmentMetricMetadataRow(_objectSpread(_objectSpread({}, entry), {}, {
    i18n,
    inlineMetric: true
  })))))) : null);
}
function renderDataCellContent(_ref8) {
  let {
    liftType,
    formattedValue,
    liftValue,
    formattedComparisonValue
  } = _ref8;
  if (!isNumber(formattedValue) && !formattedValue) {
    return "-";
  }
  if (Number.isFinite(liftValue)) {
    const formattedLiftValue = formatPercentForDataTable(liftValue);
    const comparisonTooltip = /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
      placement: Placement.Top,
      text: "".concat(formattedValue, " ").concat(LIFT_TOOLTIP_COMPARE, " ").concat(formattedComparisonValue)
    } });
    if (liftType === LiftType.Overall) {
      return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".cell-text" }, /* @__PURE__ */ jsx("span", { sel: ".cell-text.is-secondary" }, "".concat(formattedValue, " \xB7 ")), comparisonTooltip, "".concat(formattedLiftValue)));
    }
    const isPositive = liftValue > 0;
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".cell-text" }, comparisonTooltip, formattedValue, /* @__PURE__ */ jsx(TextHighlightEl, { sel: ".mp-lift-pill", attrs: {
      text: "".concat(isPositive ? "+" : "").concat(formattedLiftValue),
      theme: liftValue === 0 ? TEXT_HIGHLIGHT_THEME.NEUTRAL : isPositive ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE,
      size: SIZE.SMALL
    } })));
  }
  return formattedValue;
}
function TimeComparisonHeaderCell(_ref9) {
  let {
    column,
    tableEl
  } = _ref9;
  const gridCellWidth = 1;
  const gridRowStart = tableEl.getNumHeaderRows();
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = "".concat(gridRowStart, " / ").concat(gridColStart, " / ").concat(gridRowStart + 1, " / ").concat(gridColStart + gridCellWidth);
  let formattedDateRange = "";
  let prefix = "";
  let titleAttributeText = "";
  if (column.timeComparisonMetadata) {
    [formattedDateRange] = formatDateRanges([column.timeComparisonMetadata.dateRange]);
    prefix = column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current ? "Current" : "Past";
    titleAttributeText = "".concat(prefix, " ").concat(formattedDateRange);
  }
  const context = {
    columnIdx: column.columnIdx,
    rowType: RowType.TimeComparisonHeader
  };
  return /* @__PURE__ */ jsx("div", { attrs: {
    title: titleAttributeText
  }, class: {
    "first-in-group": column.first,
    "last-in-group": column.last,
    "first-column": column.leftmost,
    "last-column": column.rightmost,
    "last-sticky": column.lastSticky
  }, on: {
    pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
    pointerleave: tableEl.helpers.handleCellPointerLeave
  }, sel: ".mp-table-cell.mp-time-comparison-cell", style: {
    "grid-area": gridAreaStyle
  }, "data-sentry-component": "TimeComparisonHeaderCell", "data-sentry-source-file": "template.tsx" }, column.timeComparisonMetadata ? /* @__PURE__ */ jsx("div", { sel: ".time-comparison-cell-wrapper" }, /* @__PURE__ */ jsx("div", { sel: ".time-comparison-cell-content" }, /* @__PURE__ */ jsx("div", { sel: ".overflow-wrapper" }, /* @__PURE__ */ jsx("span", { sel: ".mp-time-comparison-prefix" }, prefix), formattedDateRange))) : null);
}
function renderBodyRows(_ref10) {
  let {
    tableEl,
    rows,
    columns,
    atSelectionLimit
  } = _ref10;
  return rows.map((row) => {
    return columns.map((column) => {
      return /* @__PURE__ */ jsx(BodyCell, { tableEl, row, column, atSelectionLimit });
    });
  });
}
function BodyCell(props) {
  const {
    tableEl,
    row,
    column,
    atSelectionLimit,
    segmentCellBlock
  } = props;
  const context = tableEl.helpers.getBodyCellRenderContext(row, column);
  const cellItem = tableEl.helpers.getCellItem(row, column.columnIdx);
  const empty = cellItem === null;
  const bgColor = cellItem && cellItem.backgroundColor || column.backgroundColor || null;
  const textColor = cellItem && cellItem.textColor || column.textColor || null;
  const tableTheme = tableEl.getTheme();
  const focusable = tableTheme.cellFocusable && !empty;
  let cellContent;
  let cellTooltipEl = null;
  let cellCheckboxEl = null;
  let cellCollapseEl = null;
  let isUrlSegmentCell = false;
  if (column.cellRenderer) {
    cellContent = column.cellRenderer({
      item: row,
      context
    });
  } else if (isSegmentColumn(column)) {
    if (isURL(context.label)) {
      cellContent = renderSegmentLabel(context.label);
      isUrlSegmentCell = true;
    } else {
      cellContent = context.label;
    }
  } else {
    if (tableEl.isMultimetricMode()) {
      var _row$context$liftFiel;
      cellContent = renderDataCellContent({
        liftType: tableEl.attr("lift-comparison-type"),
        formattedValue: context.label,
        liftValue: (_row$context$liftFiel = row[context.liftField]) !== null && _row$context$liftFiel !== void 0 ? _row$context$liftFiel : null,
        formattedComparisonValue: row[context.liftExtra],
        dataFormatType: column.dataFormatType
      });
    } else {
      cellContent = context.label;
    }
  }
  if (isSameCellByContext(context, tableEl.state.hoveredCellContext)) {
    let tooltipContent = null;
    const defaultTooltip = getTooltipFromLabel(context.label);
    if (cellItem !== null && cellItem !== void 0 && cellItem.tooltip) {
      tooltipContent = cellItem.tooltip;
    } else if (isSegmentColumn(column)) {
      const {
        print
      } = tableEl.getContext("i18n");
      const isUnderSegmentCellBlock = tableEl.showSegmentCellBlocks() && !segmentCellBlock && !column.lastSegment;
      if (isUnderSegmentCellBlock) {
        tooltipContent = null;
      } else if (tableEl.state.hoveredTruncatedTextType) {
        tooltipContent = isUrlSegmentCell ? context.label : print(defaultTooltip) || cellContent;
      } else {
        tooltipContent = print(defaultTooltip);
      }
    }
    const stringTooltip = typeof tooltipContent === "string";
    cellTooltipEl = Boolean(tooltipContent) && /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
      "always-show": true,
      "max-width": "".concat(DEFAULT_TOOLTIP_MAX_WIDTH, "px"),
      placement: Placement.RightCenter,
      "break-word": true,
      text: stringTooltip ? tooltipContent : ""
    } });
  }
  if (column.field !== "statSig") {
    cellContent = /* @__PURE__ */ jsx(
      "div",
      {
        class: {
          [CssClass.OverflowWrapper]: true
        },
        attrs: {
          title: !cellTooltipEl && typeof cellContent === "string" ? cellContent : ""
        },
        on: {
          pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev),
          pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
        }
      },
      cellContent
    );
  }
  if (column.hasCheckbox) {
    cellCheckboxEl = /* @__PURE__ */ jsx(CellTypeCheckbox, { row, tableEl, atSelectionLimit, context });
  }
  if (column.hasCollapseButton) {
    cellCollapseEl = /* @__PURE__ */ jsx(CellTypeCollapsible, { row, tableEl });
  }
  let gridAreaStyle = null;
  if (!segmentCellBlock) {
    const rowIdx = row.renderRowIdx + tableEl.state.renderRowStartIdx;
    const gridRowStart = tableEl.getNumHeaderRows() + rowIdx + 1;
    const gridColStart = column.subsetColumnIdx + 1;
    gridAreaStyle = "".concat(gridRowStart, " / ").concat(gridColStart, " / ").concat(gridRowStart + 1, " / ").concat(gridColStart + 1);
  }
  const isSelectedCell = focusable && tableEl.isSelectedCell(context);
  const isSelectable = column.isSelectable && !tableEl.isBoardMode();
  return /* @__PURE__ */ jsx(
    "div",
    {
      sel: ".mp-table-cell.body-cell",
      key: "".concat(row.rowIdx, ":").concat(row.groupedRowIdx, ":").concat(column.columnIdx, ":").concat(context.label),
      class: {
        "first-in-group": column.first,
        "last-in-group": column.last,
        "first-column": column.leftmost,
        "last-column": column.rightmost,
        "last-sticky": column.lastSticky,
        "sticky-column": column.sticky,
        "last-segment-col": isSegmentColumn(column) && column.lastSegment,
        "segment-col": isSegmentColumn(column),
        "segment-start-cell": context.segmentStartCell,
        "segment-end-cell": context.segmentEndCell,
        "segment-start-row": row.segmentStartRow,
        "segment-end-row": row.segmentEndRow,
        "first-segment-col": column.field === "".concat(CommonDataTableFields.SegmentPrefix, "0"),
        "mp-text-cell": isTextStyleColumn(column),
        "mp-number-cell": isNumberStyleColumn(column),
        "has-multi-select": tableEl.attr("multi-select"),
        "first-in-pair": tableEl.attr("has-comparison") && !row.isComparisonRow,
        "hovered-row": tableEl.isRowHovered(context),
        "selected-cell": isSelectedCell,
        "is-empty": empty,
        "segment-block-label": Boolean(segmentCellBlock),
        selectable: isSelectable
      },
      attrs: _objectSpread({
        elref: "bodyCell"
      }, focusable ? {
        tabindex: 0
      } : {}),
      style: _objectSpread({
        "--bg-color": bgColor,
        color: textColor,
        cursor: cellItem && cellItem.clickable || isSelectable ? "pointer" : "auto"
      }, gridAreaStyle ? {
        "grid-area": gridAreaStyle
      } : {}),
      on: {
        click: (ev) => tableEl.helpers.handleCellClick(ev, row, column.field, context, {
          textColor,
          bgColor
        }),
        pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
        pointerleave: tableEl.helpers.handleCellPointerLeave
      },
      "data-sentry-component": "BodyCell",
      "data-sentry-source-file": "template.tsx"
    },
    /* @__PURE__ */ jsx("div", { sel: ".body-cell-wrapper" }, /* @__PURE__ */ jsx("div", { sel: ".body-cell-content" }, cellCheckboxEl, cellCollapseEl, cellContent, isSelectedCell === false && cellTooltipEl), (() => {
      if (!segmentCellBlock) {
        return null;
      }
      if (!segmentCellBlock.metricMetadatas) {
        return null;
      }
      const numDescendantRows = segmentCellBlock.flatRowEndIdx - segmentCellBlock.flatRowStartIdx;
      if (numDescendantRows <= 1) {
        return null;
      }
      return renderSegmentMetricMetadatas({
        dataColumns: tableEl.getCurrentDataColumns(),
        liftType: tableEl.attr("lift-comparison-type"),
        metricMetadatas: segmentCellBlock.metricMetadatas,
        // Because of the smaller font-size / line-height for segment subtotal rows, it's always possible to
        // render `numDescendantRows` segment subtotal rows in addition to the segment label.
        numAvailableRows: numDescendantRows,
        i18n: tableEl.getContext("i18n")
      });
    })())
  );
}
function renderResizeHandles(_ref11) {
  let {
    tableEl,
    columns,
    gap,
    idxOffset,
    resizingColumnIdx,
    resizeHandleTopOffset
  } = _ref11;
  return columns.reduce((handles, column, idx) => {
    if (!column.resizable) {
      return handles;
    }
    const handleWidth = 8;
    const globalColIdx = idx + idxOffset;
    const isResizing = resizingColumnIdx === globalColIdx;
    let handleLeft = 0;
    if (gap) {
      const gapBefore = !column.lastSticky && !column.rightmost ? gap : 0;
      handleLeft = -gapBefore - handleWidth / 2;
    } else {
      const hasStickyColumnsBorder = column.lastSticky;
      handleLeft = -(handleWidth / 2) - (hasStickyColumnsBorder ? STICKY_COLUMNS_BORDER_WIDTH / 2 : 0);
    }
    return handles.concat(/* @__PURE__ */ jsx("div", { sel: ".".concat(CssClass.ColumnResizeHandle), class: {
      "is-resizing": isResizing,
      "other-resizing": Number.isFinite(resizingColumnIdx) && !isResizing
    }, style: {
      "grid-column-start": "".concat(idx + 2),
      left: "".concat(handleLeft, "px"),
      width: "".concat(handleWidth, "px"),
      top: "".concat(resizeHandleTopOffset, "px"),
      height: "calc(100% - ".concat(resizeHandleTopOffset, "px)")
    }, on: {
      mousedown: (ev) => tableEl.onResizeMouseDown({
        ev,
        resizeColumnIndex: globalColIdx
      })
    } }));
  }, []);
}
function renderSegmentCellBlocks(_ref12) {
  let {
    tableEl
  } = _ref12;
  if (!tableEl.showSegmentCellBlocks()) {
    return [];
  }
  const {
    atSelectionLimit
  } = tableEl.helpers.getGlobalSelectionStats();
  const numHeaderRows = tableEl.getNumHeaderRows();
  const {
    renderRowStartIdx,
    numRowsToRender
  } = tableEl.state;
  const renderRowEndIdx = renderRowStartIdx + numRowsToRender;
  return tableEl.state.segmentCellBlocks.filter((cellBlock) => {
    return areIntersectingRanges([cellBlock.flatRowStartIdx, cellBlock.flatRowEndIdx], [renderRowStartIdx, renderRowEndIdx]);
  }).map((cellBlock) => {
    var _tableEl$state$hovere;
    const gridRowOffset = numHeaderRows;
    const gridRowStartIdx = gridRowOffset + cellBlock.flatRowStartIdx;
    const gridRowEndIdx = gridRowOffset + cellBlock.flatRowEndIdx;
    const column = tableEl.state.columns[cellBlock.columnIdx];
    const row = tableEl.state.rows[cellBlock.rowStartIdx];
    const hoveredRowIndex = (_tableEl$state$hovere = tableEl.state.hoveredCellContext) === null || _tableEl$state$hovere === void 0 ? void 0 : _tableEl$state$hovere.rowIdx;
    const isHovered = hoveredRowIndex !== void 0 && hoveredRowIndex >= cellBlock.rowStartIdx && hoveredRowIndex < cellBlock.rowEndIdx;
    return /* @__PURE__ */ jsx("div", { sel: ".segment-cell-block", style: {
      "grid-row": "".concat(gridRowStartIdx + 1, " / ").concat(gridRowEndIdx + 1),
      "grid-column": "".concat(cellBlock.columnIdx + 1, " / ").concat(cellBlock.columnIdx + 2)
    }, class: {
      "first-col": cellBlock.columnIdx === 0,
      "hovered-segment-block": isHovered
    }, on: {
      pointerenter: tableEl.helpers.handleSegmentBlockPointerEnter
    } }, /* @__PURE__ */ jsx("div", { sel: ".segment-cell-block-inner" }, /* @__PURE__ */ jsx(BodyCell, { tableEl, row, column, atSelectionLimit, segmentCellBlock: cellBlock })));
  });
}
function renderFixedMultimetricFooter(_ref13) {
  let {
    tableEl
  } = _ref13;
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  const gridColStart = 1;
  const gridColEnd = gridColStart + tableEl.state.stickyColumnsCount;
  return /* @__PURE__ */ jsx("div", { sel: ".multimetric-footer-cell.overall-cell", style: {
    "grid-area": "".concat(gridRowStart, " / ").concat(gridColStart, " / ").concat(gridRowEnd, " / ").concat(gridColEnd)
  }, "data-sentry-component": "renderFixedMultimetricFooter", "data-sentry-source-file": "template.tsx" }, "Overall");
}
function renderDataMultimetricFooter(_ref14) {
  let {
    tableEl,
    columns
  } = _ref14;
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  return columns.map((column) => {
    const gridColStart = column.subsetColumnIdx + 1;
    const gridColEnd = gridColStart + 1;
    return /* @__PURE__ */ jsx("div", { sel: ".multimetric-footer-cell.metric-total-cell", style: {
      "grid-area": "".concat(gridRowStart, " / ").concat(gridColStart, " / ").concat(gridRowEnd, " / ").concat(gridColEnd)
    } }, renderDataCellContent({
      // The overall lift comparison value only makes sense for time comparison lift at the moment
      formattedComparisonValue: column.labelTransform(column, "comparisonOverall"),
      dataFormatType: column.dataFormatType,
      liftType: tableEl.attr("lift-comparison-type") === LiftType.Time ? LiftType.Time : null,
      liftValue: column.liftOverall,
      formattedValue: column.labelTransform(column, "metricOverall")
    }));
  });
}
function renderSearchInput(_ref15) {
  let {
    tableEl
  } = _ref15;
  return /* @__PURE__ */ jsx(InputEl, { sel: ".".concat(CssClass.SearchInput), attrs: {
    icon: "search",
    placeholder: "Search",
    size: InputSize.Medium,
    "show-clear": true
  }, props: {
    value: tableEl.state.searchFilter
  }, on: {
    clear: () => tableEl.helpers.handleSearchTextUpdate(""),
    input: (ev) => tableEl.helpers.handleSearchTextUpdate(ev.target.value)
  }, "data-sentry-element": "InputEl", "data-sentry-component": "renderSearchInput", "data-sentry-source-file": "template.tsx" });
}
function getTemplateWidths(cols) {
  return cols.map((c) => "".concat(c.computedWidth, "px")).join(" ");
}
export function template() {
  var _this$state$sorting;
  const tableTheme = this.getTheme();
  const gap = this.helpers.getGapSize();
  const groupHeaders = this.getGroupHeadersToRender();
  const fixedColumns = this.getFixedColumns();
  const dataColumns = this.getDataColumns();
  const dataColumnsToRender = this.getDataColumnsToRender();
  const fixedColumnsWidth = getSummedColumnWidth(fixedColumns);
  const internalTableWidth = this.helpers.getInternalTableWidth();
  const hasSegmentCellBlocks = this.showSegmentCellBlocks();
  const canUseResizingLayoutVal = !this.isBoardMode();
  const preTitleRowHeight = this.getPreTitleRowHeight();
  const titleRowHeight = this.getTitleRowHeight();
  const headerRowHeights = this.getHeaderRowHeights();
  const headerHeight = sum(headerRowHeights) + gap;
  const {
    dataColumnWidthsMatrix: columnWidthsMatrix,
    rowHeightsMatrix
  } = this.state;
  const headerDataColumnsToRender = this.getHeaderDataColumnsToRender();
  const dataColumnWidthsCssVar = columnWidthsMatrix.map((_ref16) => {
    let {
      width,
      count
    } = _ref16;
    return count === 1 ? "".concat(width, "px") : "repeat(".concat(count, ",").concat(width, "px)");
  }).join(" ");
  const dataColumnsWidth = getSummedColumnWidth(headerDataColumnsToRender);
  const flatRowsToRender = this.getFlatRowsToRender();
  const rowHeightsArray = [...headerRowHeights.map((x) => "".concat(x, "px")), ...rowHeightsMatrix.map((_ref17) => {
    let {
      height,
      count
    } = _ref17;
    return count === 1 ? "".concat(height, "px") : "repeat(".concat(count, ",").concat(height, "px)");
  }), ...this.showMultimetricFooter() ? ["".concat(FOOTER_ROW_HEIGHT, "px")] : []];
  const rowHeights = rowHeightsArray.join(" ");
  const useBorders = tableTheme.cellBorders === "line";
  const {
    numSelected,
    numSelectable,
    atSelectionLimit,
    hasReachedSegmentationLimit
  } = this.helpers.getGlobalSelectionStats();
  const lastFlatRow = last(flatRowsToRender);
  const showTruncatedSegmentsInfo = this.helpers.truncatedSegmentsBannerEnabled() && hasReachedSegmentationLimit && (!lastFlatRow || lastFlatRow.filteredRowIdx === this.state.filteredRows.length - 1);
  const emptySearchResults = this.state.searchFilter && !flatRowsToRender.length;
  const titleRowTopOffset = (preTitleRowHeight || 0) + gap;
  return (
    // wrap the table in order to overlay tooltips on the scrollbox
    /* @__PURE__ */ jsx("div", { sel: ".wrapper", style: {
      "--checkbox-cell-padding-left": "".concat(CHECKBOX_CELL_PADDING_LEFT, "px"),
      "--table-horizontal-padding": canUseResizingLayoutVal ? HORIZONTAL_CONTENT_PADDING_STYLE : "0"
    }, "data-sentry-component": "template", "data-sentry-source-file": "template.tsx" }, this.attr("multi-select") && !canUseResizingLayoutVal ? (() => {
      return /* @__PURE__ */ jsx("div", { sel: ".segments-info-section" }, /* @__PURE__ */ jsx("div", { sel: ".truncated-segments-info" }, /* @__PURE__ */ jsx("div", { sel: ".checkbox-wrapper" }, /* @__PURE__ */ jsx(CheckboxEl, { sel: ".select-segment-checkbox", attrs: {
        checked: atSelectionLimit,
        "unchecked-icon": numSelected > 0 ? "dash" : ""
      }, style: {
        width: "auto"
      }, on: {
        click: this.helpers.toggleAllSelected
      } }), /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
        placement: Placement.RightCenter,
        text: atSelectionLimit ? "Deselect All" : "Select Top Segments"
      } })), "Selected ".concat(numSelected, " of ").concat(hasReachedSegmentationLimit ? "top " : "").concat(numSelectable, " segments"), hasReachedSegmentationLimit ? /* @__PURE__ */ jsx(SegmentationLimitInfoEl, { attrs: {
        "segmentation-limit": this.attr("segmentation-limit"),
        "export-segmentation-limit": this.attr("export-segmentation-limit")
      } }) : null), /* @__PURE__ */ jsx(FilterBarEl, { sel: ".filter-bar", on: {
        updateInput: (ev) => this.helpers.handleSearchTextUpdate(ev.detail.value)
      } }));
    })() : null, this.isSearchEnabled() && !groupHeaders && renderSearchInput({
      tableEl: this
    }), /* @__PURE__ */ jsx("div", { sel: ".mp-data-table-container", hook: {
      insert: (vnode) => this.scrollParentElInserted(vnode.elm)
    } }, /* @__PURE__ */ jsx("div", { sel: ".mp-data-table", style: {
      "--data-table-columns": "".concat(fixedColumnsWidth, "px ").concat(dataColumnsWidth + (useBorders ? 0 : 2 * gap), "px"),
      "--fixed-columns-width": "".concat(fixedColumnsWidth, "px"),
      "--gap": "".concat(gap, "px"),
      "--body-row-height": "".concat(tableTheme.rowHeight, "px"),
      "--sticky-columns-border-width": "".concat(dataColumns.length ? STICKY_COLUMNS_BORDER_WIDTH : 0, "px"),
      "--header-height": "".concat(headerHeight, "px"),
      "--title-row-top-offset": "".concat(titleRowTopOffset, "px"),
      "--time-comparison-header-row-top-offset": "".concat(titleRowTopOffset + titleRowHeight, "px"),
      "--table-width": "".concat(internalTableWidth, "px"),
      "--row-heights": rowHeights
    }, class: {
      "use-borders": useBorders,
      "has-group-headers": Boolean(groupHeaders),
      heatmap: tableTheme.cellBackground === "heatmap",
      "cell-outline-on-hover": tableTheme.cellBorderHover === "outline",
      "multimetric-styles": tableTheme.multimetricStyles,
      "has-segment-cell-blocks": hasSegmentCellBlocks,
      "resizing-layout": canUseResizingLayoutVal,
      "show-info-footer-container": showTruncatedSegmentsInfo || emptySearchResults,
      // TODO(mack): There's some report-editor tests where sorting isn't being passed in, causing the null
      // check on sorting to be needed. Seems to be a race condition, but not exactly sure why.
      "grouped-sort": ((_this$state$sorting = this.state.sorting) === null || _this$state$sorting === void 0 ? void 0 : _this$state$sorting.sortBy) === SortBy.Column,
      "can-use-view-n": this.canUseViewN(),
      "has-time-comparison-headers": this.hasTimeComparisonHeaders()
    }, on: {
      scroll: () => this.helpers.handleScroll()
    }, hook: {
      insert: (vnode) => this.scrollElInserted(vnode.elm)
    } }, /* @__PURE__ */ jsx("div", { sel: ".".concat(CssClass.FixedColumns), class: {
      "sticky-columns": this.helpers.shouldUseStickyColumns()
    }, style: {
      "--fixed-column-widths": getTemplateWidths(fixedColumns)
    }, on: {
      pointerleave: this.helpers.handleCellPointerLeave
    } }, groupHeaders ? /* @__PURE__ */ jsx("div", { sel: ".group-header-row-cell", style: {
      "grid-row": "1 / 2",
      "grid-column": "1 / ".concat(fixedColumns.length + 1)
    } }, this.isSearchEnabled() && renderSearchInput({
      tableEl: this
    })) : null, fixedColumns.map((column) => /* @__PURE__ */ jsx(TitleCell, { tableEl: this, column })), this.hasTimeComparisonHeaders() && fixedColumns.map((column) => /* @__PURE__ */ jsx(TimeComparisonHeaderCell, { column, tableEl: this })), renderSegmentCellBlocks({
      tableEl: this
    }), renderBodyRows({
      tableEl: this,
      rows: flatRowsToRender,
      columns: fixedColumns,
      atSelectionLimit
    }), this.showMultimetricFooter() ? renderFixedMultimetricFooter({
      tableEl: this
    }) : null, renderResizeHandles({
      tableEl: this,
      columns: fixedColumns,
      gap,
      idxOffset: 0,
      resizingColumnIdx: this.state.resizeColumnIndex,
      resizeHandleTopOffset: preTitleRowHeight || 0
    })), /* @__PURE__ */ jsx("div", { sel: ".".concat(CssClass.ScrollableColumns), style: {
      "--data-column-widths": dataColumnWidthsCssVar
    }, class: {
      "is-resizing": Boolean(this.state.resizeColumnIndex)
    }, on: {
      pointerleave: this.helpers.handleCellPointerLeave
    } }, groupHeaders ? groupHeaders.reduce((acc, _ref18, idx) => {
      let {
        title,
        span,
        funnelStepNumber
      } = _ref18;
      const context = {
        rowType: RowType.GroupHeader,
        columnIdx: acc.colStart - 1
      };
      const shouldShowTooltip = isSameCellByContext(context, this.state.hoveredCellContext) && Boolean(this.state.hoveredTruncatedTextType);
      const tooltipEl = shouldShowTooltip ? /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
        // Limit the tooltip to its own cell. This is to prevent the first grouped header's tooltip
        // from being obscured by the sticky column to its left.
        "max-width": "".concat(GROUP_HEADER_MIN_WIDTH, "px"),
        placement: Placement.Bottom,
        offset: 0,
        "always-show": true,
        text: title
      } }) : null;
      return {
        nodes: [...acc.nodes, /* @__PURE__ */ jsx("div", { sel: ".group-header-row-cell.group-header", class: {
          "last-group-header": idx === groupHeaders.length - 1
        }, style: {
          "grid-row": "1 / 2",
          "grid-column": "".concat(acc.colStart, " / ").concat(acc.colStart + span)
        } }, /* @__PURE__ */ jsx("span", { sel: ".group-header-label", on: {
          pointerenter: (ev) => {
            this.helpers.handleCellPointerEnter(context);
            this.helpers.handleTruncatableTextPointerEnter(ev);
          },
          pointerleave: this.helpers.handleCellPointerLeave
        } }, funnelStepNumber ? /* @__PURE__ */ jsx("span", { sel: ".step-number" }, funnelStepNumber) : null, title), tooltipEl)],
        colStart: acc.colStart + span
      };
    }, {
      nodes: [],
      colStart: 1
    }).nodes : null, headerDataColumnsToRender.map((col) => /* @__PURE__ */ jsx(TitleCell, { tableEl: this, column: col })), this.hasTimeComparisonHeaders() && dataColumns.map((column) => /* @__PURE__ */ jsx(TimeComparisonHeaderCell, { column, tableEl: this })), renderBodyRows({
      tableEl: this,
      rows: flatRowsToRender,
      columns: dataColumnsToRender,
      atSelectionLimit
    }), this.showMultimetricFooter() ? renderDataMultimetricFooter({
      tableEl: this,
      columns: dataColumnsToRender
    }) : null), canUseResizingLayoutVal && (showTruncatedSegmentsInfo || emptySearchResults) ? /* @__PURE__ */ jsx("div", { sel: ".info-footer-container" }, emptySearchResults ? /* @__PURE__ */ jsx("div", { sel: ".empty-search-results" }, "Your search returned no results") : null, showTruncatedSegmentsInfo ? /* @__PURE__ */ jsx(BannerEl, { sel: ".truncated-segments-banner", hook: {
      insert: (vnode) => this.helpers.onInsertTruncatedSegmentsInfo(vnode.elm),
      destroy: () => this.helpers.onDestroyTruncatedSegmentsInfo()
    }, attrs: {
      size: BannerSize.Small,
      type: BannerType.Embedded,
      theme: BannerTheme.Base,
      "banner-tracking-type": "[Data Table] Truncated Segments"
    } }, /* @__PURE__ */ jsx("div", { sel: ".truncated-segments-text", attrs: {
      slot: "body-text"
    } }, "We limit the number of segments to ".concat(this.attr("segmentation-limit").toLocaleString(), ". "), /* @__PURE__ */ jsx("a", { attrs: {
      href: SegmentationLimitLearnMoreUrl,
      target: "_blank"
    } }, "Learn more"), " or ", /* @__PURE__ */ jsx("a", { attrs: {
      href: "javascript:void(0);"
    }, on: {
      click: () => this.dispatchEvent(new CustomEvent("export-csv", {
        bubbles: true,
        composed: true
      }))
    } }, "export up to ", this.attr("export-segmentation-limit").toLocaleString(), " segments via CSV"))) : null) : null)))
  );
}
